import {Box, Stack, Typography} from '@mui/material';
import React from 'react';
import ArrDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {allLevels} from './Index';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';

interface IReportPanel {
  handleClusterChange: (value: any) => void;
  clustersCount: {name: string; value: number};
  selectedCluster: {[key: number]: string | null};
  currentLevelIndex: number;
  handleLevelChange: (index: number) => void;
  docsCount: {totalDocs: number; clusterlessTextDocuments: number};
  isLoading: boolean;
  handleReset: () => void;
  handleCreate: () => void;
  keyword: string;
  isNoClustersFound: boolean;
}
const commonPath = 'reports.dataAnalysisReport.conceptExplorer.';

const ReportPanel = ({
  handleClusterChange,
  clustersCount,
  selectedCluster,
  currentLevelIndex,
  handleLevelChange,
  docsCount,
  isLoading,
  handleReset,
  handleCreate,
  keyword,
  isNoClustersFound,
}: IReportPanel) => {
  return (
    <Box className="report-panel">
      <Stack className="text-panel">
        <Stack className="panel-title">
          <Typography
            className="typo-text subhead3_size"
            tabIndex={0}
            data-testid={`cluster-level-heading${allLevels[currentLevelIndex].replace(/(st|nd|rd|th)$/, '')}`}
          >
            {t(`${commonPath}descriptionHeader`, {
              level: allLevels[currentLevelIndex],
              documents: docsCount.totalDocs,
            })}
          </Typography>
          <ArrTooltipWrapper
            title={t(`${commonPath}descriptionTooltip.title`)}
            trigger={
              <InfoIcon
                className="concept-explorer-info-icon"
                fontSize="medium"
                tabIndex={0}
                data-testid="concept-explorer-info-icon"
                aria-label={t(`${commonPath}tooltip.icon`)}
              />
            }
          >
            <p data-testid={`report-panel-p`}>
              {t(`${commonPath}descriptionTooltip.description.paragraph1`)}
            </p>
            <p data-testid={`report-panel-p-text`}>
              {t(`${commonPath}descriptionTooltip.description.paragraph2`)}
            </p>
            <p data-testid={`report-panel-p-btn`}>
              {t(`${commonPath}descriptionTooltip.description.paragraph3`)}
            </p>
            <p data-testid={`report-panel-p-button`}>
              {t(`${commonPath}descriptionTooltip.description.paragraph4`)}
            </p>
          </ArrTooltipWrapper>
        </Stack>

        <Typography
          className="typo-text body3_size"
          tabIndex={0}
          data-testid="arr-cluster-first"
        >
          {isNoClustersFound ? (
            <>
              <span data-testid={`report-panel-no-cluster`}>
                {t(`${commonPath}noteDescription.noclusterDescription`)}
              </span>
              <a href="/" data-testid={`report-panel-a`}>
                {t(`${commonPath}noteDescription.linkSpan`)}
              </a>
            </>
          ) : (
            <>
              <span data-testid={`report-panel-cluster-doc-count`}>
                {t(`${commonPath}noteDescription.span1`, {
                  clusterlessTextDocuments:
                    docsCount.clusterlessTextDocuments ?? 0,
                })}
              </span>
              {docsCount.clusterlessTextDocuments > 0 && (
                <a href="/" data-testid={`report-panel-a-link`}>
                  {t(`${commonPath}noteDescription.linkSpan`)}
                </a>
              )}
              <span data-testid={`report-panel-span-btn`}>
                {t(`${commonPath}noteDescription.span2`)}
              </span>
            </>
          )}
        </Typography>
      </Stack>
      <Stack className="show-items-panel">
        <Typography
          className="typo-text label2_size"
          data-testid={`report-panel-show`}
        >
          {t(`${commonPath}show`)}
        </Typography>
        <ArrDropdown
          sizeVariant="medium"
          dropdownLabel={''}
          selectedValues={clustersCount}
          dropdownItems={[
            {id: 1, name: 'Top 30', value: 30},
            {id: 2, name: 'Top 60', value: 60},
          ]}
          onSelectionChange={handleClusterChange}
          customWidth={12.5}
          dataTestId="clusters-count-dropdown"
        />
      </Stack>
      <Stack className="analysis-panel">
        <Stack className="keyword-section">
          <Typography
            className="typo-text label2_size"
            tabIndex={0}
            data-testid={`report-panel-keyword-text`}
          >
            {t(`${commonPath}keywordTxt`)}
          </Typography>
          <Typography
            className="typo-text label2_size"
            tabIndex={0}
            data-testid={`report-panel-key-word`}
          >
            {keyword !== '' ? keyword : t(`${commonPath}none`)}
          </Typography>
          <ArrButton
            variant="text"
            size="small"
            onClick={handleReset}
            dataTestId="report-reset-button"
            aria-label={t('common.reset')}
          >
            {t('common.reset')}
          </ArrButton>
          {keyword === '' && (
            <ArrButton
              variant="text"
              size="small"
              onClick={handleCreate}
              dataTestId="report-create-button"
              aria-label={t('common.create')}
            >
              {t('common.create')}
            </ArrButton>
          )}
        </Stack>
        <Stack className="analyze-section">
          <ArrButton
            variant="primary"
            size="small"
            onClick={() => handleLevelChange(currentLevelIndex - 1)}
            dataTestId={`btn-go-back-level${currentLevelIndex}`}
            aria-label={t(`${commonPath}analyzeBtnBack`, {
              level:
                allLevels[
                  currentLevelIndex === 0
                    ? currentLevelIndex
                    : currentLevelIndex - 1
                ],
            })}
            disabled={currentLevelIndex === 0 || isLoading}
            loading={isLoading}
            tabIndex={0}
          >
            {t(`${commonPath}analyzeBtnBack`, {
              level:
                allLevels[
                  currentLevelIndex === 0
                    ? currentLevelIndex
                    : currentLevelIndex - 1
                ],
            })}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="small"
            onClick={() => handleLevelChange(currentLevelIndex + 1)}
            dataTestId={`btn-analyze-back-level${currentLevelIndex}`}
            aria-label={t(`${commonPath}analyzeBtnTo`, {
              level:
                allLevels[
                  currentLevelIndex === allLevels.length - 1
                    ? currentLevelIndex
                    : currentLevelIndex + 1
                ],
            })}
            disabled={
              currentLevelIndex === allLevels.length - 1 ||
              selectedCluster[currentLevelIndex] === null ||
              isLoading
            }
            loading={isLoading}
            tabIndex={0}
          >
            {t(`${commonPath}analyzeBtnTo`, {
              level:
                allLevels[
                  currentLevelIndex === allLevels.length - 1
                    ? currentLevelIndex
                    : currentLevelIndex + 1
                ],
            })}
          </ArrButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default React.memo(ReportPanel);
